<template>
  <div class="admire-payment-mapping">
    <p class="admire-payment-mapping__intro">
      {{
        $t(
          "integration.admire_payment_mapping_intro",
          'Match each Charidy payment type below to the exact payment method name set up in your Admire account. In Admire, go to Settings > Billing to see your organization\'s payment methods, and edit the names below so they match exactly. Admire has no built-in "Other" method - add one there (Payment Type: Other) so the fallback below has somewhere to point.'
        )
      }}
    </p>

    <b-row>
      <b-col md="6" class="admire-payment-mapping__column">
        <h6 class="admire-payment-mapping__heading">
          {{ $t("integration.admire_payment_mapping_gateway_heading", "Payment Gateways") }}
        </h6>
        <p class="admire-payment-mapping__subheading">
          {{
            $t(
              "integration.admire_payment_mapping_gateway_subheading",
              "One row per payment gateway active on this account. A gateway added later that isn't listed here falls back to Other, below."
            )
          }}
        </p>

        <b-form-group
          v-for="bucket in gatewayBuckets"
          :key="bucket.key"
          :label="bucket.label"
          :label-for="`admire-payment-method-${bucket.key}`"
        >
          <b-form-input
            :id="`admire-payment-method-${bucket.key}`"
            v-model="mapping[bucket.key].name"
            type="text"
            :placeholder="bucket.defaultName"
          ></b-form-input>
        </b-form-group>

        <p v-if="!gatewayBuckets.length" class="admire-payment-mapping__empty">
          {{
            $t(
              "integration.admire_payment_mapping_no_gateways",
              "No active payment gateways found for this account yet."
            )
          }}
        </p>
      </b-col>

      <b-col md="6" class="admire-payment-mapping__column">
        <h6 class="admire-payment-mapping__heading">
          {{ $t("integration.admire_payment_mapping_offline_heading", "Offline Donation Types") }}
        </h6>
        <p class="admire-payment-mapping__subheading">
          {{
            $t(
              "integration.admire_payment_mapping_offline_subheading",
              "Manually-entered donations, where staff pick the actual method used."
            )
          }}
        </p>

        <b-form-group
          v-for="bucket in offlineBuckets"
          :key="bucket.key"
          :label="bucket.label"
          :label-for="`admire-payment-method-${bucket.key}`"
        >
          <b-form-input
            :id="`admire-payment-method-${bucket.key}`"
            v-model="mapping[bucket.key].name"
            type="text"
            :placeholder="bucket.defaultName"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-checkbox v-model="confirmed" class="admire-payment-mapping__confirm">
      {{
        $t(
          "integration.admire_payment_mapping_confirm",
          "I compared these names to my Admire account (Settings > Billing) and confirmed they match, editing them where needed."
        )
      }}
    </b-form-checkbox>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  ADMIRE_OFFLINE_BUCKETS,
  buildGatewayBuckets,
  getDefaultAdmirePaymentMethodMapping,
} from "./admirePaymentMethodBuckets.js";

export default {
  name: "AdmirePaymentMethodMapping",

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      mapping: {},
      hydratedKeys: new Set(),
      confirmed: false,
    };
  },

  computed: {
    ...mapState({
      gatewaysRaw: (s) => s.gateways.gateways,
    }),

    activeGatewayIds() {
      const ids = (this.gatewaysRaw || [])
        .filter((g) => g.attributes?.active)
        .map((g) => g.attributes.gateway);
      return Array.from(new Set(ids));
    },

    gatewayBuckets() {
      return buildGatewayBuckets(this.activeGatewayIds);
    },

    offlineBuckets() {
      return ADMIRE_OFFLINE_BUCKETS;
    },

    allBuckets() {
      return [...this.gatewayBuckets, ...this.offlineBuckets];
    },
  },

  watch: {
    allBuckets: {
      immediate: true,
      handler(buckets) {
        const defaults = getDefaultAdmirePaymentMethodMapping(this.gatewayBuckets);
        let changed = false;

        buckets.forEach((bucket) => {
          if (this.hydratedKeys.has(bucket.key)) {
            return;
          }
          this.hydratedKeys.add(bucket.key);
          this.$set(this.mapping, bucket.key, {
            name: this.value?.[bucket.key]?.name ?? defaults[bucket.key].name,
            id: this.value?.[bucket.key]?.id ?? "",
          });
          changed = true;
        });

        if (changed) {
          this.emitChange();
        }
      },
    },

    mapping: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },

    confirmed: {
      immediate: true,
      handler(val) {
        this.$emit("confirmed-change", val);
      },
    },
  },

  mounted() {
    this.$store.dispatch("getGateways");
  },

  methods: {
    emitChange() {
      this.$emit("input", this.mapping);
    },
  },
};
</script>

<style lang="scss" scoped>
.admire-payment-mapping__intro {
  font-size: 0.9rem;
  color: #6c757d;
}
.admire-payment-mapping__column {
  min-width: 0;
}
.admire-payment-mapping__heading {
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.admire-payment-mapping__subheading {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.75rem;
}
.admire-payment-mapping__empty {
  font-size: 0.85rem;
  color: #6c757d;
}
.admire-payment-mapping__confirm {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
</style>
